import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaDownload } from '@react-icons/all-files/fa/FaDownload';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { type ConfirmConfig, useConfirm } from '../commons/Confirm';
import {
  canPreviewNombre,
  isImageFile,
  isPdfFile,
  isTxtFile,
} from '../explorer/archivo-file-types';
import type { IProfaseArchivo } from '../explorer/profase-archivo-api';
import {
  downloadProfaseArchivoContenido,
  fetchProfaseArchivoContenido,
} from '../explorer/profase-archivo-api';
import ImageViewerModal from '../explorer/ImageViewerModal';
import PdfViewerModal from '../explorer/PdfViewerModal';
import TxtViewerModal from '../explorer/TxtViewerModal';
import {
  descargarActividadAdjuntoContenido,
  eliminarActividadAdjunto,
  fetchActividadAdjuntoBlob,
  listarActividadAdjuntos,
  listarNodoInstanciaAdjuntos,
  resolveAdjuntoDescripcion,
  resolveAdjuntoDisplayNombre,
  resolveAdjuntoArchivoData,
  resolveAdjuntoSubidoPor,
  subirActividadAdjunto,
  type IAdjuntoItem,
} from '../shared/workflow/actividad-adjuntos';

interface ActividadAdjuntosSectionProps {
  actividadId?: string | number;
  nodoInstanciaId?: string | number;
  disabled?: boolean;
  readOnly?: boolean;
  requiereArchivo?: boolean;
  uploadActionName?: string;
  /** Incrementar para recargar la lista desde el padre */
  refreshKey?: number;
  onItemsChange?: (items: IAdjuntoItem[]) => void;
  /** Muestra la confirmación en el contenedor del modal (parte superior) */
  onConfirmConfigChange?: (config: ConfirmConfig | null) => void;
}

const emptyUploadForm = () => ({
  descripcion: '',
});

function adjuntoItemToArchivo(item: IAdjuntoItem): IProfaseArchivo {
  const nombre = resolveAdjuntoDisplayNombre(item);
  return {
    id: item.archivo?.id,
    nombre: nombre === '—' ? (item.archivo?.nombre ?? 'archivo') : nombre,
    path: item.archivo?.path ?? '',
    esDirectorio: false,
    data: item.archivo?.data,
  };
}

export default function ActividadAdjuntosSection({
  actividadId,
  nodoInstanciaId,
  disabled = false,
  readOnly = false,
  requiereArchivo = false,
  uploadActionName = 'save',
  refreshKey = 0,
  onItemsChange,
  onConfirmConfigChange,
}: ActividadAdjuntosSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<IAdjuntoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadNombre, setUploadNombre] = useState('');
  const [uploadForm, setUploadForm] = useState(emptyUploadForm);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busyItemId, setBusyItemId] = useState<number | null>(null);
  const [busyAction, setBusyAction] = useState<'download' | 'delete' | null>(null);
  const [previewItem, setPreviewItem] = useState<IAdjuntoItem | null>(null);
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    onConfirmConfigChange?.(confirmConfig);
  }, [confirmConfig, onConfirmConfigChange]);

  useEffect(() => () => {
    onConfirmConfigChange?.(null);
  }, [onConfirmConfigChange]);

  const syncItems = useCallback((nextItems: IAdjuntoItem[]) => {
    setItems(nextItems);
    onItemsChange?.(nextItems);
  }, [onItemsChange]);

  const usesActividadApi = actividadId != null && actividadId !== '';

  const reloadAdjuntos = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (usesActividadApi) {
        const adjunto = await listarActividadAdjuntos(actividadId as string | number);
        syncItems(adjunto?.items ?? []);
      } else if (nodoInstanciaId != null && nodoInstanciaId !== '') {
        const items = await listarNodoInstanciaAdjuntos(nodoInstanciaId);
        syncItems(items);
      } else {
        syncItems([]);
      }
    } catch (e: unknown) {
      syncItems([]);
      setLoadError(e instanceof Error ? e.message : 'No se pudieron cargar los adjuntos.');
    } finally {
      setLoading(false);
    }
  }, [actividadId, nodoInstanciaId, syncItems, usesActividadApi]);

  useEffect(() => {
    void reloadAdjuntos();
  }, [reloadAdjuntos, refreshKey]);

  const handleFilePick = useCallback((file: File | null) => {
    setUploadFile(file);
    setUploadError(null);
    if (file) {
      setUploadNombre(file.name);
    } else {
      setUploadNombre('');
    }
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFilePick(file);
  }, [handleFilePick]);

  const openFilePicker = useCallback(() => {
    if (disabled || readOnly || uploading || !inputRef.current) return;
    inputRef.current.value = '';
    inputRef.current.click();
  }, [disabled, readOnly, uploading]);

  const resetUploadForm = useCallback(() => {
    setUploadFile(null);
    setUploadNombre('');
    setUploadForm(emptyUploadForm());
    setUploadError(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const handleUpload = useCallback(async () => {
    if (!usesActividadApi || actividadId == null) {
      setUploadError('No se puede subir archivos en este contexto.');
      return;
    }
    if (!uploadFile) {
      setUploadError('Seleccione un archivo.');
      return;
    }
    const nombre = uploadNombre.trim() || uploadFile.name;
    if (!nombre) {
      setUploadError('Indique un nombre para el adjunto.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      await subirActividadAdjunto(actividadId, {
        file: uploadFile,
        nombre,
        descripcion: uploadForm.descripcion,
        actionName: uploadActionName,
      });
      resetUploadForm();
      await reloadAdjuntos();
    } catch (e: unknown) {
      setUploadError(e instanceof Error ? e.message : 'No se pudo subir el archivo.');
    } finally {
      setUploading(false);
    }
  }, [
    actividadId,
    reloadAdjuntos,
    resetUploadForm,
    uploadActionName,
    uploadFile,
    uploadForm.descripcion,
    uploadNombre,
    usesActividadApi,
  ]);

  const handleDownload = useCallback(async (item: IAdjuntoItem) => {
    setBusyItemId(item.id ?? null);
    setBusyAction('download');
    setLoadError(null);
    try {
      if (usesActividadApi && item.id != null && actividadId != null) {
        await descargarActividadAdjuntoContenido(
          actividadId,
          item.id,
          resolveAdjuntoDisplayNombre(item),
        );
      } else if (item.archivo?.id != null) {
        await downloadProfaseArchivoContenido(adjuntoItemToArchivo(item));
      } else {
        throw new Error('No se puede descargar el archivo.');
      }
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'No se pudo descargar el archivo.');
    } finally {
      setBusyItemId(null);
      setBusyAction(null);
    }
  }, [actividadId, usesActividadApi]);

  const performDelete = useCallback(async (itemId: number) => {
    if (!usesActividadApi || actividadId == null) return;
    setBusyItemId(itemId);
    setBusyAction('delete');
    setLoadError(null);
    setConfirmLoading(true);
    try {
      const adjunto = await eliminarActividadAdjunto(actividadId, itemId);
      syncItems(adjunto.items ?? []);
      if (previewItem?.id === itemId) {
        setPreviewItem(null);
      }
      dismiss();
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'No se pudo eliminar el adjunto.');
      dismiss();
    } finally {
      setBusyItemId(null);
      setBusyAction(null);
      setConfirmLoading(false);
    }
  }, [actividadId, dismiss, previewItem?.id, setConfirmLoading, syncItems]);

  const handleDelete = useCallback((item: IAdjuntoItem) => {
    if (item.id == null || readOnly) return;
    const nombre = resolveAdjuntoDisplayNombre(item);
    ask(
      <>¿Eliminar el adjunto <strong>{nombre}</strong>? Esta acción no se puede deshacer.</>,
      () => { void performDelete(item.id as number); },
      {
        title: 'Eliminar adjunto',
        confirmLabel: 'Eliminar',
        variant: 'danger',
      },
    );
  }, [ask, performDelete, readOnly]);

  const previewArchivo = useMemo(
    () => (previewItem ? adjuntoItemToArchivo(previewItem) : null),
    [previewItem],
  );

  const previewFetchContent = useMemo(() => {
    if (previewItem == null) return undefined;
    const nombre = resolveAdjuntoDisplayNombre(previewItem);
    if (usesActividadApi && previewItem.id != null && actividadId != null) {
      const itemId = previewItem.id;
      return () => fetchActividadAdjuntoBlob(actividadId, itemId);
    }
    if (previewItem.archivo?.id != null) {
      const archivoId = previewItem.archivo.id;
      return async () => {
        const { blob } = await fetchProfaseArchivoContenido(archivoId, nombre);
        return blob;
      };
    }
    return undefined;
  }, [actividadId, previewItem, usesActividadApi]);

  const previewNombre = previewArchivo?.nombre ?? '';
  const uploadBusy = disabled || readOnly || uploading;
  const listBusy = disabled || uploading;
  const showRequiredHint = !readOnly && requiereArchivo && !loading && items.length === 0;

  return (
    <div className={`actividad-adjuntos${readOnly ? ' actividad-adjuntos--readonly' : ''}`}>
      <div className="actividad-adjuntos__header">
        <h3 className="actividad-adjuntos__title">Archivos adjuntos</h3>
        {!readOnly && requiereArchivo ? (
          <span className="actividad-adjuntos__badge">Requerido</span>
        ) : null}
      </div>

      {!readOnly ? (
      <div className="actividad-adjuntos__upload">
        <p className="actividad-adjuntos__upload-label">Agregar archivo</p>
        <input
          ref={inputRef}
          type="file"
          className="actividad-adjuntos__file-input"
          disabled={uploadBusy}
          onChange={handleFileInputChange}
        />
        <div className="actividad-adjuntos__descripcion-row">
          <button
            type="button"
            className="profase-form-file-pick actividad-adjuntos__select-btn"
            disabled={uploadBusy}
            onClick={openFilePicker}
          >
            <span style={{ fontSize: 15 }}>📎</span>
            Seleccionar
          </button>
          {uploadFile ? (
            <div className="actividad-adjuntos__nombre-wrap">
              <input
                id={`actividad-adjuntos-nombre-${actividadId}`}
                type="text"
                className="actividad-adjuntos__nombre-input"
                value={uploadNombre}
                disabled={uploadBusy}
                placeholder="Nombre del archivo"
                title={uploadNombre}
                onChange={e => setUploadNombre(e.target.value)}
              />
              <button
                type="button"
                className="profase-form-file-remove"
                disabled={uploadBusy}
                onClick={resetUploadForm}
                title="Quitar archivo"
                aria-label="Quitar archivo"
              >
                <FaTimes size={11} />
              </button>
            </div>
          ) : null}
          <label
            htmlFor={`actividad-adjuntos-descripcion-${actividadId}`}
            className="actividad-adjuntos__descripcion-label"
          >
            Descripción
          </label>
          <textarea
            id={`actividad-adjuntos-descripcion-${actividadId}`}
            className="actividad-adjuntos__descripcion-input"
            value={uploadForm.descripcion}
            rows={1}
            disabled={uploadBusy || !uploadFile}
            placeholder="Descripción opcional"
            onChange={e => setUploadForm(prev => ({ ...prev, descripcion: e.target.value }))}
          />
          <button
            type="button"
            className="actividad-adjuntos__upload-btn"
            disabled={uploadBusy || !uploadFile}
            onClick={() => { void handleUpload(); }}
          >
            {uploading ? 'Subiendo…' : 'Subir archivo'}
          </button>
        </div>

        {uploadError ? (
          <p className="actividad-adjuntos__error">{uploadError}</p>
        ) : null}
      </div>
      ) : null}

      {showRequiredHint ? (
        <p className="actividad-adjuntos__hint actividad-adjuntos__hint--warn">
          Esta actividad requiere al menos un archivo adjunto.
        </p>
      ) : null}

      {loadError ? (
        <p className="actividad-adjuntos__error">{loadError}</p>
      ) : null}

      {loading ? (
        <p className="actividad-adjuntos__placeholder">Cargando adjuntos…</p>
      ) : items.length === 0 ? (
        <p className="actividad-adjuntos__placeholder">Sin archivos adjuntos.</p>
      ) : (
        <div className="actividad-adjuntos__list-wrap">
          <table className="actividad-adjuntos__table">
            <tbody>
              {items.map(item => {
                const itemId = item.id;
                const itemBusy = itemId != null && busyItemId === itemId;
                const nombre = resolveAdjuntoDisplayNombre(item);
                const descripcion = resolveAdjuntoDescripcion(item);
                const archivoData = resolveAdjuntoArchivoData(item);
                const { email } = resolveAdjuntoSubidoPor(item);
                const canPreview = canPreviewNombre(nombre);
                const nombreTitle = descripcion ? `${nombre}\n${descripcion}` : nombre;
                return (
                  <tr key={itemId ?? `${nombre}-${item.archivo?.id ?? 'row'}`}>
                    <td title={nombreTitle}>{nombre}</td>
                    <td title={archivoData || undefined}>{archivoData || '—'}</td>
                    <td className="actividad-adjuntos__email-cell" title={email || undefined}>
                      {email || '—'}
                    </td>
                    <td>
                      <div className="actividad-adjuntos__row-actions">
                        <button
                          type="button"
                          className="actividad-adjuntos__action-btn"
                          disabled={listBusy || itemBusy || (usesActividadApi ? itemId == null : item.archivo?.id == null)}
                          onClick={() => { void handleDownload(item); }}
                          title="Descargar archivo"
                          aria-label={`Descargar ${nombre}`}
                        >
                          <FaDownload size={12} />
                          {itemBusy && busyAction === 'download' ? '…' : 'Descargar'}
                        </button>
                        {canPreview ? (
                          <button
                            type="button"
                            className="actividad-adjuntos__action-btn actividad-adjuntos__action-btn--preview"
                            disabled={listBusy || (usesActividadApi ? itemId == null : item.archivo?.id == null)}
                            onClick={() => setPreviewItem(item)}
                            title="Vista previa"
                            aria-label={`Vista previa de ${nombre}`}
                          >
                            <FaEye size={12} />
                          </button>
                        ) : null}
                        {!readOnly ? (
                          <button
                            type="button"
                            className="actividad-adjuntos__action-btn actividad-adjuntos__action-btn--danger"
                            disabled={listBusy || itemBusy || itemId == null || !usesActividadApi}
                            onClick={() => handleDelete(item)}
                            title="Eliminar adjunto"
                            aria-label={`Eliminar ${nombre}`}
                          >
                            <FaTrash size={11} />
                            {itemBusy && busyAction === 'delete' ? '…' : 'Eliminar'}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PdfViewerModal
        open={previewItem != null && isPdfFile(previewNombre)}
        archivo={previewArchivo}
        fetchContent={previewFetchContent}
        onClose={() => setPreviewItem(null)}
      />
      <TxtViewerModal
        open={previewItem != null && isTxtFile(previewNombre)}
        archivo={previewArchivo}
        fetchContent={previewFetchContent}
        onClose={() => setPreviewItem(null)}
      />
      <ImageViewerModal
        open={previewItem != null && isImageFile(previewNombre)}
        archivo={previewArchivo}
        fetchContent={previewFetchContent}
        onClose={() => setPreviewItem(null)}
      />
    </div>
  );
}
